// AdminBottomNavV2 — admin's mobile bottom nav.
//
// Per-section colours (kept verbatim from the previous incarnation;
// only the URL each tab targets changed in the 2026-05-16 Routing
// Restructure):
//
//   '/'          Home     → violet  (admin brand)
//   '/people/players'  Players  → sky
//   '/people/agents'   Agents   → emerald
//   '/offers'    Offers   → amber  (was /rewards before rename)
//   '/settings'  Settings → indigo (was /more before rename)
//
// Tab order 2026-08-08 (UX Phase 0): Players before Agents everywhere
// (matches the broker end + the TopPillNav order). Section keys renamed
// (earlier) from `rewards`/
// `more` to `offers`/`settings` so the rest of the code uses the new
// nomenclature consistently.
//
// Hidden on lg+ screens; TopPillNav takes over there.

import { useEffect, useRef, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import { useAdminAuth } from '../lib/adminAuth';
import AdminAvatar from './AdminAvatar';
import Icon from '../../components/Icon';

// 2026-05-16 (Routing Restructure): SectionKey renamed `rewards` →
// `offers` and `more` → `settings`. Colour ramps unchanged — only
// the keys (and the URLs each tab targets) updated.
type SectionKey = 'home' | 'people' | 'gamemoney' | 'offers' | 'settings';

type Tab = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
  section: SectionKey;
  permissionKey?: string;
};

const ADMIN_TABS: Tab[] = [
  // 2026-08-08 (UX Phase 0): Players before Agents (matches broker + TopPillNav).
  // Earlier (2026-05-16 Routing Restructure): Rewards → Offers, More → Settings.
  { to: '/',                label: 'Home',       icon: 'dashboard',     section: 'home' },
  { to: '/people/players',  label: 'People',     icon: 'group',         matchPrefix: '/people',      section: 'people' },
  { to: '/game-money',      label: 'Game money', icon: 'payments',      matchPrefix: '/game-money',   section: 'gamemoney' },
  { to: '/offers',          label: 'Offers',     icon: 'card_giftcard', matchPrefix: '/offers',       section: 'offers' },
  { to: '/settings',        label: 'Settings',   icon: 'settings',      matchPrefix: '/settings',     section: 'settings' },
];

const AGENT_TABS: Tab[] = [
  // Same swap as ADMIN_TABS so the rare path where AdminAppShellV2
  // routes an agent through this surface stays consistent.
  { to: '/',                label: 'Home',     icon: 'dashboard',     section: 'home' },
  { to: '/people/players',  label: 'Players',  icon: 'group',         matchPrefix: '/people/players', section: 'people', permissionKey: 'users' },
  { to: '/people/agents',   label: 'Agents',   icon: 'support_agent', matchPrefix: '/people/agents',  section: 'people',  permissionKey: 'create_subagent' },
  { to: '/offers',          label: 'Offers',   icon: 'card_giftcard', matchPrefix: '/offers',         section: 'offers' },
  { to: '/settings',        label: 'Settings', icon: 'settings',      matchPrefix: '/settings',       section: 'settings' },
];

// Section colour ramps — same structural shape as AgentBottomNav's
// SECTION_COLORS. Light + dark gradient sets, label colours, and rgb
// triplets for the bubble's drop-shadow glow. Picked so each tab's
// bubble matches the section's accent the user already associates
// with that area of the admin app.
const SECTION_COLORS: Record<SectionKey, {
  gradLight: string;
  gradDark: string;
  labelLight: string;
  labelDark: string;
  rgba: string;
  rgbaDark: string;
}> = {
  // Home wears VIOLET — admin's brand identity, same hue the TopPillNav
  // active pill uses, so the user sees one unified accent at top + bottom.
  home: {
    gradLight: 'from-violet-400 via-violet-500 to-violet-600',
    gradDark:  'from-violet-500 via-violet-500 to-violet-600',
    labelLight: 'text-violet-700',
    labelDark:  'text-violet-300',
    rgba:     '139,92,246',
    rgbaDark: '167,139,250',
  },
  // People (Players / Agents / Bots) wears SKY — matches the PlayersHub
  // header ring + the People segment bar's active pill. (UX Phase 4: the
  // former separate players/agents sections merged into one People tab.)
  people: {
    gradLight: 'from-sky-400 via-sky-500 to-sky-600',
    gradDark:  'from-sky-500 via-sky-400 to-sky-300',
    labelLight: 'text-sky-700',
    labelDark:  'text-sky-300',
    rgba:     '14,165,233',
    rgbaDark: '56,189,248',
  },
  // Game money wears TEAL — its own nav identity, distinct from the emerald
  // used inside the hub content. (UX Phase 4: promoted to a top-level tab.)
  gamemoney: {
    gradLight: 'from-teal-400 via-teal-500 to-teal-600',
    gradDark:  'from-teal-500 via-teal-400 to-teal-300',
    labelLight: 'text-teal-700',
    labelDark:  'text-teal-300',
    rgba:     '20,184,166',
    rgbaDark: '45,212,191',
  },
  // 2026-05-16: renamed from `rewards` (URL was /rewards).
  offers: {
    gradLight: 'from-yellow-500 via-amber-500 to-amber-600',
    gradDark:  'from-amber-400 via-yellow-300 to-yellow-200',
    labelLight: 'text-yellow-800',
    labelDark:  'text-yellow-300',
    rgba:     '245,158,11',
    rgbaDark: '253,224,71',
  },
  // 2026-05-16: renamed from `more` (URL was /more).
  settings: {
    gradLight: 'from-indigo-400 via-indigo-500 to-indigo-600',
    gradDark:  'from-indigo-500 via-indigo-500 to-indigo-600',
    labelLight: 'text-indigo-700',
    labelDark:  'text-indigo-300',
    rgba:     '99,102,241',
    rgbaDark: '129,140,248',
  },
};

// Geometry constants copied from AgentBottomNav / BottomNav so the
// physical proportions stay in lockstep across all three SPAs.
const NAV_HEIGHT_PX = 52;
const BUBBLE_DIAMETER_PX = 42;
const NOTCH_RADIUS_PX = 32;
const BOUNCY_SPRING = { type: 'spring' as const, stiffness: 450, damping: 14, mass: 1 };

export default function AdminBottomNavV2() {
  const { admin, canDo } = useAdminAuth();
  const loc = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const [activeX, setActiveX] = useState(-200);

  // Single shared motion value. Bubble translate and mask gradient
  // stop both read from this — guaranteed lockstep on every frame.
  const x = useMotionValue(activeX);

  // Role-appropriate tab set (filtered by permission).
  const baseTabs = admin?.role === 'agent' ? AGENT_TABS : ADMIN_TABS;
  const tabs = useMemo(
    () => baseTabs.filter((t) => !t.permissionKey || canDo(t.permissionKey, 'view')),
    [baseTabs, canDo],
  );

  const activeIx = tabs.findIndex((t) => {
    if (t.matchPrefix) return loc.pathname === t.to || loc.pathname.startsWith(t.matchPrefix + '/') || loc.pathname === t.matchPrefix;
    return loc.pathname === t.to;
  });
  const activeTab = activeIx >= 0 ? tabs[activeIx] : null;
  const activeColors = activeTab ? SECTION_COLORS[activeTab.section] : SECTION_COLORS.home;

  // Measure active LI's centre x in nav-local coordinates.
  // ResizeObserver covers viewport changes; activeIx covers route changes.
  useEffect(() => {
    if (!admin || !ulRef.current || !navRef.current) return;
    const measure = () => {
      const li = ulRef.current!.children[activeIx] as HTMLElement | undefined;
      if (!li) { setActiveX(-200); return; }
      const liRect = li.getBoundingClientRect();
      const navRect = navRef.current!.getBoundingClientRect();
      setActiveX(liRect.left - navRect.left + liRect.width / 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(navRef.current);
    ro.observe(ulRef.current);
    return () => ro.disconnect();
  }, [activeIx, admin]);

  // Drive the motion value with the bouncy spring whenever activeX
  // changes. Returning controls.stop() in the cleanup cancels any in-
  // flight animation if activeX moves again before settling.
  useEffect(() => {
    const controls = animate(x, activeX, BOUNCY_SPRING);
    return () => controls.stop();
  }, [activeX, x]);

  // Mask-image string derived from `x`. useTransform creates a motion
  // value that maps `x` → gradient string; passing it to the style
  // prop makes motion update the mask-image on every animation frame.
  const maskImage = useTransform(
    x,
    (cx) => `radial-gradient(circle ${NOTCH_RADIUS_PX}px at ${cx}px 0px, transparent ${NOTCH_RADIUS_PX - 1}px, black ${NOTCH_RADIUS_PX}px)`,
  );

  if (!admin) return null;

  return (
    <nav
      ref={navRef}
      // lg:hidden — desktop uses TopPillNav. Safe-area-inset-bottom
      // absorbs the iOS home indicator zone.
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden
                 pb-[env(safe-area-inset-bottom)]"
    >
      {/* Real cutout + masked top border.
          mask-image makes the cutout area transparent so the page
          content behind the nav shows through. The `border-t` is
          applied HERE (on the masked element) so it gets cut by the
          same mask: the straight horizontal line has a clean ~64px gap
          exactly at the cutout. Surface + border tokens copied verbatim
          from AgentBottomNav. */}
      <motion.div
        aria-hidden="true"
        style={{ maskImage, WebkitMaskImage: maskImage }}
        className="absolute inset-0
                   bg-white dark:bg-arena-800
                   backdrop-blur
                   border-t border-arena-200/60 dark:border-arena-600/60"
      />

      {/* Curved arc following the cutout's bottom rim — driven by the
          SAME motion value so arc + cutout + bubble all jiggle together. */}
      <motion.div
        aria-hidden="true"
        style={{
          x,
          marginLeft: -NOTCH_RADIUS_PX,
          width: NOTCH_RADIUS_PX * 2,
          height: NOTCH_RADIUS_PX,
          top: 0,
          borderBottomLeftRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
          borderBottomRightRadius: `${NOTCH_RADIUS_PX}px ${NOTCH_RADIUS_PX}px`,
        }}
        className="absolute left-0 z-20 pointer-events-none
                   border-b border-arena-200/60 dark:border-arena-600/60"
      />

      {/* Tab list — active tab's icon is hidden (visibility: hidden);
          the bubble overlay below shows the icon at the same x. */}
      <ul
        ref={ulRef}
        className="relative z-30 max-w-xl mx-auto flex justify-around items-end"
        style={{ height: NAV_HEIGHT_PX }}
      >
        {tabs.map((t, ix) => {
          const isActive = ix === activeIx;
          // The More tab swaps the generic 'menu' glyph for the admin's
          // avatar image only on the agent role — matches the agent
          // bottom-nav pattern. Admin role keeps the menu icon (admin
          // accounts have a dedicated /profile entry inside the More
          // page already, no need to surface the avatar at this level).
          const isAvatarTab = false;
          return (
            <li key={t.to} className="relative flex-1 h-full">
              <NavLink
                to={t.to}
                end={t.to === '/'}
                className="relative flex flex-col items-center justify-end h-full outline-none"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Flat icon — visible only when this tab is NOT active. */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-[10px]
                             flex items-center justify-center pointer-events-none"
                  style={{ visibility: isActive ? 'hidden' : 'visible' }}
                >
                  {isAvatarTab ? (
                    <AdminAvatar
                      url={admin.avatar_url}
                      username={admin.username}
                      size="xs"
                      className="ring-1 ring-arena-300/70 dark:ring-arena-600/70"
                    />
                  ) : (
                    <Icon
                      name={t.icon}
                      size={22}
                      filled
                      weight={400}
                      className="text-arena-500 dark:text-neon-200/60"
                    />
                  )}
                </div>

                {/* Label — section colour on active, muted otherwise. */}
                <span
                  className={`absolute bottom-1 left-1/2 -translate-x-1/2
                              text-[11px] leading-none transition-all
                              ${isActive
                                ? `font-extrabold tracking-wide ${SECTION_COLORS[t.section].labelLight} dark:${SECTION_COLORS[t.section].labelDark}`
                                : 'font-medium text-arena-500 dark:text-neon-200/60'}`}
                >
                  {t.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* Bubble overlay — ONE motion.div for the entire nav. style={{x}}
          reads the SAME motion value the cutout's mask reads from, so
          they always stay in lockstep no matter how bouncy the spring.
          Per-section gradient + glow rgba flip on every tab change. */}
      {activeTab && (
        <motion.div
          style={{
            x,
            marginLeft: -(BUBBLE_DIAMETER_PX / 2),
            top: -(BUBBLE_DIAMETER_PX / 2),
            width: BUBBLE_DIAMETER_PX,
            height: BUBBLE_DIAMETER_PX,
            // Per-section glow — arbitrary value so it can read the
            // `rgba` triplet from SECTION_COLORS at render time.
            filter: `drop-shadow(0 4px 12px rgba(${activeColors.rgba}, 0.45))`,
          }}
          animate={{ scale: 1.15 }}
          transition={BOUNCY_SPRING}
          className={`absolute left-0 z-50 rounded-full
                      flex items-center justify-center
                      bg-gradient-to-br ${activeColors.gradLight}
                      dark:bg-gradient-to-br dark:${activeColors.gradDark}
                      ring-2 ring-white/80 dark:ring-arena-900/80
                      pointer-events-none`}
        >
          <Icon
            name={activeTab.icon}
            size={28}
            filled
            weight={600}
            className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
          />
        </motion.div>
      )}
    </nav>
  );
}
